
import React from 'react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { DebugInfo } from '../types/debugTypes';

interface ErrorDisplayProps {
  error: Error;
  debugInfo: DebugInfo | null;
  showDebugInfo: boolean;
  setShowDebugInfo: (show: boolean) => void;
  companyName: string;
  companyWebsite: string;
}

export function ErrorDisplay({
  error,
  debugInfo,
  showDebugInfo,
  setShowDebugInfo,
  companyName,
  companyWebsite
}: ErrorDisplayProps) {
  return (
    <div className="w-full mt-4">
      <Collapsible 
        open={showDebugInfo} 
        onOpenChange={setShowDebugInfo}
        className="w-full border border-red-200 rounded-md"
      >
        <div className="bg-red-50 p-4 rounded-t-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="font-medium text-red-800">Enrichment Failed</span>
          </div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              {showDebugInfo ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {showDebugInfo ? 'Hide' : 'Show'} Details
            </Button>
          </CollapsibleTrigger>
        </div>
        
        <CollapsibleContent className="p-4 bg-white border-t border-red-100">
          <h4 className="font-medium mb-2">Error Message</h4>
          <pre className="bg-red-50 p-3 rounded text-red-800 text-sm overflow-auto max-h-32 mb-3">
            {error.message}
          </pre>
          
          {debugInfo && (
            <>
              <h4 className="font-medium mb-2">Debug Information</h4>
              <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto max-h-96">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </>
          )}
          
          <div className="mt-4">
            <h4 className="font-medium mb-2">Request Details</h4>
            <div className="bg-gray-50 p-3 rounded">
              <p><strong>Company:</strong> {companyName}</p>
              <p><strong>Website:</strong> {companyWebsite || 'Not provided'}</p>
              <p><strong>Timestamp:</strong> {new Date().toLocaleString()}</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
